import {Text} from '@dropbox/dig-components/typography';
import cx from 'classnames';
import DomPurify from 'dompurify';

import styles from './manager_feedback.module.css';

/**
 * Extracts plain text from HTML string by removing all tags.
 * Used as a fallback when HTML sanitization might remove content.
 */
const extractTextFromHtml = (html: string): string => {
  return html
    .replace(/<[^>]*>/g, ' ') // Replace tags with spaces
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; entities
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
};

export const TextBubble = ({html, text}: {html?: string; text?: string}) => {
  // Sanitize HTML with expanded allowlist and KEEP_CONTENT to preserve text
  const purifiedHtml =
    html &&
    DomPurify.sanitize(html, {
      ALLOWED_TAGS: [
        // Text formatting
        'a',
        'br',
        'b',
        'p',
        'span',
        'strong',
        'em',
        'i',
        'u',
        's',
        'sub',
        'sup',
        // Block elements
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'pre',
        'code',
        'hr',
        // Lists
        'ul',
        'ol',
        'li',
        // Tables
        'table',
        'thead',
        'tbody',
        'tfoot',
        'tr',
        'td',
        'th',
        'caption',
      ],
      ALLOWED_ATTR: [
        'href',
        'target',
        'rel',
        'style', // Preserves inline formatting like font-size, color
      ],
      KEEP_CONTENT: true, // KEY: Keep text content even when removing unknown tags
    });

  // Check if purified HTML has actual text content
  const purifiedTextContent = purifiedHtml
    ? extractTextFromHtml(purifiedHtml)
    : '';

  // Fallback: If original HTML had content but purified version is empty,
  // extract plain text to ensure no content is lost
  if (html && !purifiedTextContent) {
    const fallbackText = extractTextFromHtml(html);
    if (fallbackText) {
      return (
        <Text
          variant="paragraph"
          tagName="p"
          className={cx(styles['bubble-text'])}
        >
          {fallbackText}
        </Text>
      );
    }
  }

  return (
    <Text
      variant="paragraph"
      tagName="p"
      className={cx(styles['bubble-text'])}
      dangerouslySetInnerHTML={
        purifiedHtml
          ? {
              __html: purifiedHtml,
            }
          : undefined
      }
    >
      {text}
    </Text>
  );
};
